import React from "react";
import logo from "./logo.svg";
import "./App.css";

import { ConfigProvider } from "antd";
import AlertPopup from "./components/AlertPopup";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import locale from "antd/locale/ko_KR";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import * as Common from "./commons/common";
import PageLayout from "./layout/PageLayout";
import Login from "./pages/Login";
import Welcome from "./pages/Welcome";
import { useAuthState } from "./provider/AuthProvider";
import Main from "./pages/Main";
import RentInfo from "./pages/RentInfo";
import ProcessLayout from "./layout/ProcessLayout";
import UseCheckList from "./pages/UseCheckList";
import UseAddImage from "./pages/UseAddImage";
import UseEducation from "./pages/UseEducation";
import ReturnBatteryState from "./pages/ReturnBatteryState";
import ReturnAddImage from "./pages/ReturnAddImage";
import ReturnCheckList from "./pages/ReturnCheckList";
import DigitalKeyController from "./pages/DigitalKeyController";
import FindEVStation from "./pages/FindEVStation";
import ReturnAddPaymentItem from "./pages/ReturnAddPaymentItem";
import ReturnResultPayments from "./pages/ReturnResultPayments";
import ReturnAddDelayedArrivePaymentItem from "./pages/ReturnAddDelayedArrivePaymentItem";
import ReturnAddBatteryPaymentItem from "./pages/ReturnAddBatteryPaymentItem";
import ReturnAddNormalPaymentItem from "./pages/ReturnAddNormalPaymentItem";
import DigitalKeyList from "./pages/DigitalKeyList";
import RentCarsLocation from "./pages/RentCarsLocation";
import Manual from "./pages/Manual";

const PrivateRoute = (props: any) => {
    const userDetails = useAuthState();
    const location = useLocation();

    if (props.role !== undefined && props.role.length > 0) {
        const checkRole = userDetails?.user && props.role.includes(userDetails?.user.authority);
        return checkRole ? (
            props.children
        ) : (
            <PageLayout>
                <Login />
            </PageLayout>
        );
    }

    return userDetails?.user ? props.children : <Navigate to={Common.PAGE_LOGIN} replace={true} />;
};

function App() {
    return (
        <ConfigProvider
            locale={locale}
            theme={{
                token: {
                    fontFamily: "Noto Sans KR, sans-serif",
                },
            }}
        >
            <AlertPopup />

            <Routes>
                <Route path="/" element={<Navigate to={Common.PAGE_WELCOME} />} />
                <Route path={Common.PAGE_WELCOME} element={<Welcome />}></Route>
                <Route
                    path={Common.PAGE_LOGIN}
                    element={
                        <PageLayout headerType={{ leftType: "none", title: "로그인" }}>
                            <Login />
                        </PageLayout>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_MAIN}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ leftType: "logo" }} showFooter={true}>
                                <Main />
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_RENT_DETAIL + "/:rentId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "예약 상세보기" }} showFooter={true} overflow={false}>
                                <RentInfo />
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_USE_CHECKLIST + "/:rentId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "출차 비품 체크하기" }}>
                                <ProcessLayout bottomText="다음">
                                    <UseCheckList />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_USE_ADDIMAGE + "/:rentId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "차량 촬영" }}>
                                <ProcessLayout bottomText="다음">
                                    <UseAddImage />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_USE_EDUCATION + "/:rentId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "출차 안내" }}>
                                <ProcessLayout bottomText="출차 완료하기">
                                    <UseEducation />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>

                <Route
                    path={Common.PAGE_RETURN_BATTERY + "/:rentId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "배터리 상태" }}>
                                <ProcessLayout bottomText="다음">
                                    <ReturnBatteryState />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_RETURN_ADDIMAGE + "/:rentId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "사진 촬영" }}>
                                <ProcessLayout bottomText="다음">
                                    <ReturnAddImage />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_RETURN_CHECKLIST + "/:rentId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "추가 정산" }}>
                                <ProcessLayout bottomText="다음">
                                    <ReturnCheckList />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>

                <Route
                    path={Common.PAGE_RETURN_ADD_PAYMENT_ITEM + "/:rentId" + "/:categoryId"}
                    element={
                        <PrivateRoute>
                            <PageLayout>
                                <ProcessLayout bottomText="추가완료">
                                    <ReturnAddPaymentItem />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>

                <Route
                    path={Common.PAGE_RETURN_ADD_DELAYED_ARRIVE_PAYMENT_ITEM + "/:rentId" + "/:categoryId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "반납 지연" }}>
                                <ProcessLayout bottomText="추가완료">
                                    <ReturnAddDelayedArrivePaymentItem />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>

                <Route
                    path={Common.PAGE_RETURN_ADD_BATTERY_PAYMENT_ITEM + "/:rentId" + "/:categoryId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "배터리 미충전" }}>
                                <ProcessLayout bottomText="추가완료">
                                    <ReturnAddBatteryPaymentItem />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>

                <Route
                    path={Common.PAGE_RETURN_ADD_NORMAL_PAYMENT_ITEM + "/:rentId" + "/:categoryId"}
                    element={
                        <PrivateRoute>
                            <PageLayout>
                                <ProcessLayout bottomText="추가완료">
                                    <ReturnAddNormalPaymentItem />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>

                <Route
                    path={Common.PAGE_RETURN_RESULT_PAYMENT + "/:rentId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "결제 확인" }}>
                                <ProcessLayout bottomText="반납 완료하기">
                                    <ReturnResultPayments />
                                </ProcessLayout>
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_DIGITALKEY_LIST}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "디지털키" }} showFooter={true}>
                                <DigitalKeyList />
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_DIGITALKEY_CONTROL + "/:carSharerId"}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "디지털키" }} showFooter={true}>
                                <DigitalKeyController />
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_FIND_EV_STATION}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "충전소 찾기" }} showFooter={true}>
                                <FindEVStation />
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_CAR_LOCATION}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "차량 위치" }} showFooter={true}>
                                <RentCarsLocation />
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_MANUAL}
                    element={
                        <PrivateRoute>
                            <PageLayout headerType={{ title: "매뉴얼" }} showFooter={true}>
                                <Manual />
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
                <Route
                    path={Common.PAGE_MANUAL + "/:manualContent"}
                    element={
                        <PrivateRoute>
                            <PageLayout showFooter={true}>
                                <Manual />
                            </PageLayout>
                        </PrivateRoute>
                    }
                ></Route>
            </Routes>
        </ConfigProvider>
    );
}

export default App;
